import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MCQOption } from "../content-types";
import { Explanation, Markup, OptionRow } from "./shared";
import { stableShuffle } from "./mcq-shuffle";
import type { QuestionRendererProps } from "./types";

interface SocraticNode {
  node_id: string;
  question: string;
  options: MCQOption[];
  correct_option_id: string;
  next_if_correct?: string; // empty string / absent = terminal
  next_if_wrong?: string;
  explanation?: string;
}

interface SocraticChainContent {
  root_node_id: string;
  topic_concept?: string;
  nodes: SocraticNode[];
  explanation?: string;
}

const LETTERS = "ABCDEFGH";

/** socratic_chain — a stateful branching walk through a node graph (web mirror
 *  of iOS `SocraticChainView`). Each node is a mini-MCQ that reveals its own
 *  explanation, then Continue routes via next_if_correct / next_if_wrong;
 *  `NR`-prefixed nodes are remediation detours. Reaching a terminal sets the
 *  answer (true = ended on a correct pick), which enables the session's Check.
 *  Dangling/malformed pointers count as success so the session never blocks. */
export function SocraticChainRenderer({
  itemId,
  content,
  answer,
  setAnswer,
  revealed,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as SocraticChainContent;
  const nodes = c.nodes ?? [];

  const [currentNodeId, setCurrentNodeId] = useState(c.root_node_id);
  const [visited, setVisited] = useState<string[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [nodeRevealed, setNodeRevealed] = useState(false);

  const completed = typeof answer === "boolean";
  const node = nodes.find((n) => n.node_id === currentNodeId);
  const isRemediation = currentNodeId?.startsWith("NR");

  // Degenerate chain (root id resolves to nothing): count it as complete so the
  // learner is never hard-blocked — mirrors iOS.
  useEffect(() => {
    if (!completed && !node) setAnswer(true);
  }, [completed, node, setAnswer]);

  const options = useMemo(
    () => stableShuffle(node?.options ?? [], `${itemId || ""}:${currentNodeId}`),
    [node, itemId, currentNodeId],
  );

  const stateFor = (id: string): "neutral" | "selected" | "correct" | "wrong" => {
    if (!nodeRevealed) return selectedOptionId === id ? "selected" : "neutral";
    if (id === node?.correct_option_id) return "correct";
    if (selectedOptionId === id) return "wrong";
    return "neutral";
  };

  const advance = () => {
    if (!node) return;
    const isCorrect = selectedOptionId === node.correct_option_id;
    const nextId = (isCorrect ? node.next_if_correct : node.next_if_wrong) || "";
    setVisited((v) => [...v, currentNodeId]);
    if (!nextId) {
      setAnswer(isCorrect); // terminal — outcome = last node's correctness
      return;
    }
    if (!nodes.some((n) => n.node_id === nextId)) {
      setAnswer(true); // dangling pointer — never block the session
      return;
    }
    setCurrentNodeId(nextId);
    setSelectedOptionId(null);
    setNodeRevealed(false);
  };

  const restart = () => {
    setAnswer(null);
    setCurrentNodeId(c.root_node_id);
    setVisited([]);
    setSelectedOptionId(null);
    setNodeRevealed(false);
  };

  const dots = (
    <div className="flex items-center gap-1.5">
      {visited.map((_, i) => (
        <span key={i} className="h-2 w-2 rounded-full bg-brand-500" />
      ))}
      {!completed && (
        <span className="h-2.5 w-2.5 rounded-full border-2 border-brand-500 bg-brand-100" />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-brand-100 px-2.5 py-1 text-xs font-semibold text-brand-700">
          {t("Socratic chain")}
          {c.topic_concept ? ` · ${c.topic_concept}` : ""}
        </span>
        {dots}
      </div>

      {completed ? (
        <div className="mt-4 rounded-2xl bg-black/[0.04] p-5 text-center">
          <div className="text-3xl">{answer === true ? "⭐" : "🔁"}</div>
          <div className="mt-2 text-base font-bold text-ink">
            {answer === true ? t("Chain complete!") : t("Keep practising")}
          </div>
          <p className="mt-1 text-sm text-ink-soft">
            {answer === true
              ? t("You worked through the full reasoning chain. Excellent!")
              : t("You reached a branch point. Review the concepts and try again.")}
          </p>
          {answer === false && !revealed && (
            <button
              type="button"
              onClick={restart}
              className="mt-3 w-full rounded-xl bg-amber-500/10 py-2.5 text-sm font-semibold text-amber-600 hover:bg-amber-500/20"
            >
              {t("Try again")}
            </button>
          )}
        </div>
      ) : node ? (
        <div className="mt-4">
          {isRemediation && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-600">
              <span aria-hidden>↩</span>
              {t("Let's revisit this concept")}
            </div>
          )}

          <div className="whitespace-pre-wrap text-lg font-semibold leading-snug text-ink">
            <Markup>{node.question}</Markup>
          </div>

          <div className="mt-4 space-y-2">
            {options.map((o, i) => (
              <OptionRow
                key={o.id}
                state={stateFor(o.id)}
                disabled={nodeRevealed}
                onClick={() => {
                  if (nodeRevealed) return;
                  setSelectedOptionId(o.id);
                  setNodeRevealed(true);
                }}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-soft">
                  {LETTERS[i] ?? o.id}
                </span>
                <span className="text-ink">
                  <Markup>{o.text}</Markup>
                </span>
              </OptionRow>
            ))}
          </div>

          {nodeRevealed && (
            <>
              <Explanation text={node.explanation} />
              <button
                type="button"
                onClick={advance}
                className="mt-4 w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white hover:bg-brand-700"
              >
                {(selectedOptionId === node.correct_option_id
                  ? node.next_if_correct
                  : node.next_if_wrong)
                  ? t("Continue")
                  : t("Finish chain")}
              </button>
            </>
          )}
        </div>
      ) : null}

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
